// 🔒 PRIVATE MEDIA — per-chat cover state, web edition.
//
// Two independent facts, kept apart exactly as the app keeps them
// (allo-app/stores/privateMedia.ts):
//
//   ON     "this chat's media should be covered" — a durable user setting.
//   REVEAL "show me this" — a fact about this browser and this moment. Never
//          stored. Dies when the viewer closes and when the tab stops being
//          visible. No timer: a reveal lasts exactly as long as you're looking.
//          Two shapes: one message (click its cover), or the whole chat while
//          the "Reveal media" switch is on. A computer has no fingerprint
//          reader worth relying on, so the switch IS the gate — the cover's job
//          is to stop the person beside you reading over your shoulder, and a
//          deliberate flick does that.
//
// ── WHERE "ON" LIVES, AND WHY THERE ARE TWO PLACES ──────────────────────────
//   synced   chats/{id}.security.{myUid}.privateMedia   ← the app's own field
//   local    localStorage `allo_pm_local_v1`            ← this browser only
//
// Turning it on from the PHONE writes the synced field, so the web covers the
// chat automatically — no question asked, that's the app's setting arriving.
// Turning it on HERE asks first: everywhere (write the synced field, the phone
// covers too) or this browser only (never leaves the machine).
//
// effective ON = synced === true || local === true
//
// 🟥 `undefined` MEANS "NO INFORMATION". Only an explicit `false` turns the
//    cover off. Firestore serves cache-sourced snapshots whose data can be
//    missing whole fields; collapsing undefined into false would uncover a
//    chat on a bad connection and persist that. The app carries the same guard.

import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

const LOCAL_KEY = 'allo_pm_local_v1';

const syncedByChat = new Map();   // chatId -> true | false
const localByChat = new Map();    // chatId -> true
const unlocked = new Set();       // individually revealed message ids
const revealedChats = new Set();  // chats whose "Reveal media" switch is on
const listeners = new Set();

try {
  const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
  for (const k of Object.keys(raw)) if (raw[k] === true) localByChat.set(k, true);
} catch {}

function persistLocal() {
  try {
    const obj = {};
    localByChat.forEach((v, k) => { if (v) obj[k] = true; });
    localStorage.setItem(LOCAL_KEY, JSON.stringify(obj));
  } catch {}
}
function emit() { listeners.forEach((l) => { try { l(); } catch {} }); }
export function subscribe(l) { listeners.add(l); return () => listeners.delete(l); }

/* ── reads ────────────────────────────────────────────────────────────────── */
export const isPrivateMediaOn = (chatId) => !!chatId
  && (syncedByChat.get(String(chatId)) === true || localByChat.get(String(chatId)) === true);
export const isSyncedOn = (chatId) => syncedByChat.get(String(chatId)) === true;
export const isLocalOn = (chatId) => localByChat.get(String(chatId)) === true;
export const isMessageUnlocked = (id) => !!id && unlocked.has(String(id));
export const isChatRevealed = (chatId) => !!chatId && revealedChats.has(String(chatId));
/** The only question a bubble ever asks. */
export const isCovered = (chatId, messageId) =>
  isPrivateMediaOn(chatId) && !isChatRevealed(chatId) && !isMessageUnlocked(messageId);

/* ── writes ───────────────────────────────────────────────────────────────── */
/** Mirror the SYNCED setting in from a chat snapshot. Silent when unchanged. */
export function mirrorSynced(chatId, on) {
  if (!chatId) return;
  if (typeof on !== 'boolean') return;      // unknown — the snapshot didn't say
  const id = String(chatId);
  if (syncedByChat.get(id) === on && syncedByChat.has(id)) return;
  syncedByChat.set(id, on);
  unlocked.clear(); revealedChats.clear();   // never inherit a reveal across a flip
  emit();
}

/** Watch one chat's synced flag (the app's field) and keep the store warm. */
export function watchPrivateMedia(chatId, uid) {
  if (!chatId || !uid) return () => {};
  return onSnapshot(doc(db, 'chats', chatId), (s) => {
    mirrorSynced(chatId, s.data()?.security?.[uid]?.privateMedia === true
      ? true
      : (s.data()?.security?.[uid] && 'privateMedia' in (s.data().security[uid] || {}) ? false : undefined));
  }, () => {});
}

/** Turn it on/off for EVERY device — the app's own field. */
export async function setSynced(chatId, uid, on) {
  mirrorSynced(chatId, on);                  // optimistic: the cover must move now
  try {
    await updateDoc(doc(db, 'chats', chatId), { [`security.${uid}.privateMedia`]: !!on });
  } catch {
    mirrorSynced(chatId, !on);               // revert
    throw new Error('write failed');
  }
}

/** Turn it on/off for THIS BROWSER only. Never leaves the machine. */
export function setLocal(chatId, on) {
  const id = String(chatId);
  if (on) localByChat.set(id, true); else localByChat.delete(id);
  unlocked.clear(); revealedChats.clear();
  persistLocal();
  emit();
}

/** Reveal one message — clicking its cover. */
export function unlockMessage(id) {
  if (!id) return;
  unlocked.add(String(id));
  emit();
}

/** The "Reveal media" switch: uncover this whole chat until it's flicked back
 *  (or the tab goes away). Nothing is written — it dies with the session. */
export function setChatRevealed(chatId, on) {
  if (!chatId) return;
  const id = String(chatId);
  if (on) revealedChats.add(id); else revealedChats.delete(id);
  if (!on) unlocked.clear();
  emit();
}

/** Viewer closed → re-cover the individually revealed items, but LEAVE the
 *  "Reveal media" switch alone: that one is a deliberate state the user set and
 *  closing a photo isn't a request to undo it. */
export function relockItems() {
  if (!unlocked.size) return;
  unlocked.clear();
  emit();
}

/** Cover everything again, switch included: tab hidden or window blurred. */
export function relockAll() {
  if (!unlocked.size && !revealedChats.size) return;
  unlocked.clear();
  revealedChats.clear();
  emit();
}

// the cover is back before you tab away, switch apps, or a screenshot tool fires
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') relockAll();
  });
  window.addEventListener('blur', relockAll);
}
